// @ts-ignore
// eslint-disable-next-line import/no-unresolved
import { isSupported as isLoomSupported } from '@loomhq/record-sdk/is-supported'
import { colorFacetsNeutralDark300 } from '@netlify/netlify-design-tokens'
import { Code, Strong } from 'evergreen-ui'
import dynamic from 'next/dynamic'
import { useEffect, useState } from 'react'

import DockButton from '~/components/DockButton'
import DockMenuPopover from '~/components/DockMenuPopover'
import DockShareLink from '~/components/DockShareLink'
import Flex from '~/components/Flex'
import HeadsUpNotifications from '~/components/HeadsUpNotifications'
import Image from '~/components/Image'
import Tool from '~/components/Tool'
import ToolGroup from '~/components/ToolGroup'
import {
  CameraIcon,
  GitClosedIcon,
  GitCommitIcon,
  GitDraftIcon,
  GitMergeIcon,
  GitPullIcon,
  LoomIcon,
  MobileVideoIcon,
} from '~/icons/evergreen'
import { useActions, useStore } from '~/store/hooks'
import { notificationForRecording, notificationForScreenshot } from '~/utils/notifications'
import { stopRecording } from '~/utils/recorder'
import { truncate } from '~/utils/string'
import { toggleDrawer, toggleRecord, toggleScreenshot } from '~/utils/toggles'

const LoomWrapper = dynamic(() => import('~/components/LoomWrapper'), {
  ssr: false,
  loading: () => <Tool active={false} disabled iconBefore={<LoomIcon />} />,
})

type Gem = {
  color: string
  label: string
  icon: Function
}

export default function DockStateLoggedIn() {
  const merge = useStore(state => state.ref.merge)
  const toggles = useStore(actions => actions.hud.toggles)
  const service = useStore(state => state.tickets.service)
  const connections = useStore(state => state.identeer.connections)
  const notifications = useStore(state => state.notifications.items)
  const open = useStore(state => state.hud.toggles.open)

  const toggle = useActions(actions => actions.hud.toggle)
  const toggleOn = useActions(actions => actions.hud.toggleOn)
  const toggleOff = useActions(actions => actions.hud.toggleOff)
  const setGif = useActions(actions => actions.hud.setGif)
  const setImageData = useActions(actions => actions.hud.setImageData)
  const schedule = useActions(actions => actions.notifications.schedule)
  const clear = useActions(actions => actions.notifications.clear)

  const [loomError, setLoomError] = useState<boolean | undefined>(undefined)
  const [mouseDownCoords, setMouseDownCoords] = useState({ x: 0, y: 0 })
  const [mouseUpCoords, setMouseUpCoords] = useState({ x: 0, y: 0 })

  const shouldShowLoom = !!connections.find(c => c.slug === 'loom') && !loomError

  useEffect(() => {
    async function init() {
      const { supported, error } = await isLoomSupported()
      if (!supported) {
        setLoomError(error)
      }
    }
    init()
  }, [])

  const screenshotNotification = (num?: number) => {
    if (!num) {
      return
    }
    schedule(notificationForScreenshot(num))
  }

  const recordingNotification = () => {
    const n = notificationForRecording(() => {
      stopRecording()
      toggleOff('recording')
    })
    schedule(n)
  }

  const clearNotifications = () => {
    clear()
    toggleOff('recording')
    toggleOff('countdown')
  }

  const shortcuts = [
    {
      disabled: toggles.annotate || toggles.watching,
      active: false,
      iconBefore: <CameraIcon />,
      title: 'Take a screenshot',
      name: 'screenshot',
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onClick: () => {
        if (toggles.annotate) {
          toggleOff('annotate')
        } else if (toggles.countdown) {
          // no-op for now?
        } else {
          toggleScreenshot(toggleOn, toggleOff, screenshotNotification, setImageData, clearNotifications)
        }
      },
    },
    {
      disabled: toggles.annotate || toggles.watching,
      active: false,
      iconBefore: shouldShowLoom ? <LoomIcon /> : <MobileVideoIcon />,
      title: shouldShowLoom ? 'Loom' : 'Record screen',
      name: shouldShowLoom ? 'loom' : 'record',
      onClick: shouldShowLoom
        ? () => {
            toggleOff('open')
            toggleOn('loom')
          }
        : () => {
            toggleOff('open')
            if (toggles.watching) {
              toggleOff('watching')
            } else if (toggles.recording) {
              // no-op for now?
            } else {
              toggleRecord(toggles, toggleOn, toggleOff, setGif, service, recordingNotification, clearNotifications)
            }
          },
    },
  ].filter(Boolean)

  const getGem = (): Gem | undefined => {
    if (merge.draft) {
      return { color: '#6e7781', label: 'Draft', icon: GitDraftIcon }
    }
    const gemMap = {
      open: { color: '#2ca450', label: 'Open', icon: GitPullIcon },
      opened: { color: '#2ca450', label: 'Open', icon: GitPullIcon },
      merged: { color: '#8250df', label: 'Merged', icon: GitMergeIcon },
      closed: { color: '#cf212e', label: 'Closed', icon: GitClosedIcon },
    }
    return gemMap[merge.state]
  }

  const gem = getGem()

  const getLabel = () => {
    if (merge.type === 'pull_request') {
      return merge.number ? (
        <Flex>
          <Strong marginRight={8}>Netlify Drawer</Strong>
          {notifications.length > 0 ? (
            <HeadsUpNotifications />
          ) : (
            <>
              {gem && <gem.icon color={gem.color} marginRight={4} size={16} />}
              <Strong className="is-hidden-mobile" maxWidth={320} overflow="hidden" textOverflow="ellipsis">
                {merge.title}
              </Strong>
              <Strong color={colorFacetsNeutralDark300} marginX={4}>
                #{merge.number}
              </Strong>
            </>
          )}
        </Flex>
      ) : (
        // Pending Reviewers see this
        'Netlify Drawer'
      )
    }

    return (
      <Flex>
        <Strong marginRight={8}>Netlify Drawer</Strong>
        {notifications.length > 0 ? (
          <HeadsUpNotifications />
        ) : (
          <>
            <GitCommitIcon marginRight={4} size={12} />
            <Code fontSize={12} marginRight={8}>
              {truncate(merge.base.sha, 7)}
            </Code>
            <Strong className="is-hidden-mobile" maxWidth={320} overflow="hidden" textOverflow="ellipsis">
              {merge.title}
            </Strong>
          </>
        )}
      </Flex>
    )
  }

  return (
    <>
      <Flex
        className="drag-handle"
        flexGrow={1}
        justifyContent="flex-start"
        onMouseDown={e => {
          setMouseDownCoords({ x: e.clientX, y: e.clientY })
        }}
        onMouseUp={e => {
          setMouseUpCoords({ x: e.clientX, y: e.clientY })
        }}
      >
        <ToolGroup>
          <Tool
            active={false}
            ariaLabel={merge.number ? `Deploy Preview #${merge.number}` : 'Deploy Preview'}
            iconBefore={<Image alt="Logo" height={24} src="/img/logo/netlify.svg" width={24} />}
            label={getLabel()}
            onClick={() => {
              const deltaX = Math.abs(mouseDownCoords.x - mouseUpCoords.x)
              const deltaY = Math.abs(mouseDownCoords.y - mouseUpCoords.y)
              if (deltaX <= 5 && deltaY <= 5) {
                toggleDrawer(toggles, toggle)
              }
            }}
          />
        </ToolGroup>
      </Flex>
      <Flex>
        {!toggles.countdown && !toggles.recording && (
          <ToolGroup border="left" isHiddenMobile>
            {shortcuts.map(shortcut =>
              shortcut.name === 'loom' ? (
                <LoomWrapper key={shortcut.name}>
                  <Tool id="loom" {...shortcut} />
                </LoomWrapper>
              ) : (
                <Tool key={shortcut.name} {...shortcut} />
              )
            )}
          </ToolGroup>
        )}
        {open && <DockMenuPopover />}
        <DockShareLink />
        <DockButton border="left" />
      </Flex>
    </>
  )
}
